import React, { useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { User, Check, ChevronsUpDown } from 'lucide-react';
import UserAvatar from './UserAvatar';

/**
 * Searchable user (teammate) picker. Drop-in replacement for the native
 * <select> when the user list grows past a handful of entries.
 *
 * Value semantics:
 *  - ''   → "Anyone" (default)
 *  - 'me' → current user
 *  - any user id → that teammate
 *
 * The component does NOT know who "me" is; the caller supplies `currentUserId`
 * so we can filter the duplicate out of the list.
 */
const UserPicker = ({
  users = [],
  currentUserId,
  value = '',
  onChange,
  label = 'Uploaded by',
  placeholder = 'Anyone',
  testid = 'user-picker',
}) => {
  const [open, setOpen] = useState(false);

  const teammates = (users || []).filter((u) => u && u.id && u.name && u.id !== currentUserId);

  let triggerLabel;
  if (value === 'me') triggerLabel = `${label}: Me`;
  else if (value && value !== '') {
    const match = teammates.find((u) => u.id === value);
    triggerLabel = match ? `${label}: ${match.name}` : `${label}: …`;
  } else {
    triggerLabel = `${label}: ${placeholder}`;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          data-testid={testid}
          className="flex items-center gap-2 h-10 w-[200px] px-2.5 rounded-md border border-slate-200 bg-white text-sm text-slate-800 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-200"
        >
          <User className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="truncate flex-1 text-left">{triggerLabel}</span>
          <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] min-w-[260px] p-0"
        align="start"
        data-testid={`${testid}-popover`}
      >
        <Command>
          <CommandInput
            placeholder="Search teammate…"
            data-testid={`${testid}-search`}
          />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>No teammates match that search.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__anyone__"
                keywords={["anyone", "all", "everyone", "any"]}
                onSelect={() => { onChange?.(''); setOpen(false); }}
                data-testid={`${testid}-option-anyone`}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Check className={`w-3.5 h-3.5 flex-shrink-0 ${value === '' ? 'text-teal-600' : 'opacity-0'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">Anyone</div>
                  <div className="text-[11px] text-slate-500">No uploader filter</div>
                </div>
              </CommandItem>
              <CommandItem
                value="__me__"
                keywords={["me", "myself", "my uploads", "mine"]}
                onSelect={() => { onChange?.('me'); setOpen(false); }}
                data-testid={`${testid}-option-me`}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Check className={`w-3.5 h-3.5 flex-shrink-0 ${value === 'me' ? 'text-teal-600' : 'opacity-0'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">Me</div>
                  <div className="text-[11px] text-slate-500">Only my uploads</div>
                </div>
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading="Teammates">
              {teammates.map((u) => (
                <CommandItem
                  key={u.id}
                  value={u.id}
                  keywords={[u.name || '', u.email || '']}
                  onSelect={() => { onChange?.(u.id); setOpen(false); }}
                  data-testid={`${testid}-option-${u.id}`}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Check className={`w-3.5 h-3.5 flex-shrink-0 ${value === u.id ? 'text-teal-600' : 'opacity-0'}`} />
                  <UserAvatar name={u.name} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate" title={u.name}>{u.name}</div>
                    {u.email ? (
                      <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                    ) : null}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default UserPicker;
